/**
 * Background-prefetch the JS chunks for the admin pages users hit most
 * often. Called from Admin.jsx with a setTimeout-delayed requestIdleCallback
 * so it runs AFTER the dashboard has rendered and the first paint is
 * committed — never racing the initial route's render.
 *
 * Why: every page is React.lazy() so its chunk only starts downloading
 * the moment the user clicks the sidebar. In Vite dev that download +
 * on-demand transform takes 1–3s, which is the "URL changed but page
 * still loading" lag. By warming the cache while the user is reading
 * the dashboard, the chunk is already there when they click — page
 * renders immediately, only the API call gates content.
 *
 * Lazy chunks are content-addressed by Vite (?v=<hash>) so the prefetched
 * module is byte-identical to what React.lazy() will fetch later — the
 * second import resolves from cache instantly, no extra network.
 *
 * Catches are no-ops: a failed background prefetch must never throw
 * (it would crash the dashboard) — the real fetch on click will surface
 * the real error if anything is genuinely broken.
 */

const TOP_ADMIN_ROUTES = [
  // Top-level CRM pages — by far the highest navigation volume.
  () => import("../Views/Admin/Client/Customer"),
  () => import("../Views/Admin/Client/CustomerDetails"),
  () => import("../Views/Admin/Quotes/Quote"),
  () => import("../Views/Admin/Contract/Contract"),
  () => import("../Views/Admin/Invoice/Invoice"),
  () => import("../Views/Admin/ServiceRequests/index"),
  // Schedule + dashboard satellites.
  () => import("../Views/Admin/Schedual/Schedual/index"),
  () => import("../Views/Shared/TodaysAppointments/Index"),
  // POS + Payment Methods modal target.
  () => import("../Views/Admin/POS/PointOfSale"),
];

let prefetched = false;

export function prefetchAdminRoutes() {
  if (prefetched) return;
  prefetched = true;

  const run = () => {
    for (const importer of TOP_ADMIN_ROUTES) {
      try {
        importer().catch(() => {});
      } catch {
        // Static-time errors (rare; import() returns a promise) get swallowed
        // so a single broken module can't block the rest of the queue.
      }
    }
  };

  // requestIdleCallback when the browser supports it (Chrome/Edge/Firefox);
  // fall back to setTimeout(1500) on Safari, which doesn't ship rIC.
  if (typeof window === "undefined") return;
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(run, { timeout: 4000 });
  } else {
    setTimeout(run, 1500);
  }
}
